import { ChangeProjectionService, ReviewService, Roi, RoiChange } from '@analysis/domain';
import { type RoiChangesRepository, type RoiRepository } from '../../ports';

export interface SyncReviewedRoiChangesPayload {
  sampleId: string;
  currentAnalystId: string;
  sourceAnalystId: string;
}

export interface SyncReviewedRoiChangesResult {
  updatedRois: Roi[];
}

export class SyncReviewedRoiChangesUseCase {
  constructor(private readonly roiChanges: RoiChangesRepository, private readonly rois: RoiRepository) {}

  public async execute({
    sampleId,
    currentAnalystId,
    sourceAnalystId,
  }: SyncReviewedRoiChangesPayload): Promise<SyncReviewedRoiChangesResult> {
    if (sourceAnalystId === currentAnalystId) return { updatedRois: [] };

    // Fetch baseline and project source analyst's pending changes
    const baselineSourceRois = await this.rois.fetchForAnalystInSample({
      sampleId,
      analystId: sourceAnalystId,
    });

    const sourceChanges = await this.roiChanges.getForSampleAndAnalyst(sampleId, sourceAnalystId);
    const sourceRois =
      sourceChanges.length > 0
        ? ChangeProjectionService.projectChanges(baselineSourceRois, sourceChanges)
        : baselineSourceRois;

    const currentUserRois = await this.rois.fetchForAnalystInSample({
      sampleId,
      analystId: currentAnalystId,
    });

    const copyResult = ReviewService.copyRois(
      Array.from(sourceRois.values()),
      currentAnalystId,
      Array.from(currentUserRois.values())
    );

    const timestamp = Date.now();

    const currentUserRoisArray = Array.from(currentUserRois.values());

    const roiChanges: RoiChange[] = [
      ...copyResult.copiedRois.map((roi) => ({
        id: roi.id,
        sampleId: roi.sampleId,
        assetId: roi.assetId,
        creatorId: roi.creatorId,
        timestamp,
        labelsAdded: Array.from(roi.labels.keys()),
        labelsRemoved: [],
        isNew: true,
      })),
      ...currentUserRoisArray.map((roi) => ({
        id: roi.id,
        sampleId: roi.sampleId,
        assetId: roi.assetId,
        creatorId: roi.creatorId,
        timestamp,
        labelsAdded: [],
        labelsRemoved: Array.from(roi.labels.keys()),
      })),
    ];

    const updatedRois = await this.rois.updateRois({
      unchangedRois: [],
      createdRois: copyResult.copiedRois,
      updatedRois: [],
      deletedRois: currentUserRoisArray,
      roiChanges,
    });

    // Clean up all pending changes for both analysts
    const currentAnalystChanges = await this.roiChanges.getForSampleAndAnalyst(sampleId, currentAnalystId);

    const allChangeIds = [...sourceChanges.map((c) => c.id), ...currentAnalystChanges.map((c) => c.id)];

    if (allChangeIds.length > 0) {
      await this.roiChanges.deleteRoiChanges(allChangeIds);
    }

    return { updatedRois };
  }
}
